// Adding eslint disable to reduce changes to this legacy file
/* eslint-disable no-restricted-imports */
/* eslint-disable react-perf/jsx-no-new-function-as-prop */
/* eslint-disable react-perf/jsx-no-new-object-as-prop */
/* eslint-disable react-perf/jsx-no-jsx-as-prop */
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { faDrone } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { List } from "antd";
import React, { createContext, useContext, useEffect, useMemo, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { useFragment, usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";

import { Divider, Input, Text, useDebouncedState } from "@skydio/rivet-ui";

import AnnotationCategoryFilter, {
  useAnnotationsFromURL,
} from "components/common/filters/AnnotationCategoryFilter";
import Pagination, { useRelayPagination } from "components/common/Pagination";
import DockIcon from "components/DockIcon";
import FileCard from "components/main/files/FileCard";
import { IMAGE_WIDTH, PHOTO_HEIGHT, VIDEO_HEIGHT } from "components/main/files/MediaViewConstants";
import FlightAnnotations from "components/main/fleet/components/FlightAnnotations";
import { FlightMetaDataItem } from "components/main/fleet/FlightListItem";
import { useFlightDuration } from "hooks/useFlightDuration";
import { useQueryStringState } from "hooks/useQueryStringState";
import { useReverseGeocode } from "hooks/useReverseGeocode";
import { formatOrgPrefixedPath, formatPilotName } from "utils/common";

import AdminSearchEmptyState from "./AdminSearchEmptyState";

import styles from "../AdminSearchPage.module.scss";

import type { PreloadedQuery } from "react-relay";
import type { FlightsList_flight$key } from "__generated__/FlightsList_flight.graphql";
import type { FlightsList_flights$key } from "__generated__/FlightsList_flights.graphql";
import type { FlightsListAdminQuery } from "__generated__/FlightsListAdminQuery.graphql";
import type { FlightsRefetchQuery } from "__generated__/FlightsRefetchQuery.graphql";

export const query = graphql`
  query FlightsListAdminQuery(
    $count: Int!
    $cursor: String
    $searchTerm: String
    $annotationCategoryUuids: [UUID]
  ) {
    ...FlightsList_flights
      @arguments(
        count: $count
        cursor: $cursor
        searchTerm: $searchTerm
        annotationCategoryUuids: $annotationCategoryUuids
      )
  }
`;

const flightsFragment = graphql`
  fragment FlightsList_flights on Query
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String" }
    searchTerm: { type: "String" }
    annotationCategoryUuids: { type: "[UUID]" }
  )
  @refetchable(queryName: "FlightsRefetchQuery") {
    flights(
      searchTerm: $searchTerm
      annotationCategoryUuids: $annotationCategoryUuids
      first: $count
      after: $cursor
    ) @connection(key: "FlightsList_flights") {
      totalCount
      edges {
        node {
          ...FlightsList_flight
        }
      }
    }
  }
`;

const flightFragment = graphql`
  fragment FlightsList_flight on Flight {
    id
    uuid
    dataFiles(
      first: 1
      kinds: [
        VEHICLE_VIDEO_RAW
        VEHICLE_VIDEO_PREVIEW
        VEHICLE_IMAGE
        VEHICLE_IMAGE_DNG
        VEHICLE_IR_IMAGE
        VEHICLE_RADIOMETRIC_IMAGE
      ]
    ) {
      edges {
        node {
          id
          kind
          ...FileCard_file
        }
      }
    }
    organization {
      id
      uuid
      name
    }
    ...useFlightDuration_fragment
    ...FlightAnnotations_flight
    hasTelemetry
    takeoffClientType
    user {
      id
      email
      displayName
    }
    takeoffLocation {
      latitude
      longitude
    }
    vehicle {
      id
      vehicleId
      displayName
    }
    dock {
      id
      dockId
      displayName
    }
  }
`;

const pageSize = 10;

const PHOTO_SIZE = {
  height: `${PHOTO_HEIGHT}px`,
  width: `${IMAGE_WIDTH}px`,
};
const VIDEO_SIZE = {
  height: `${VIDEO_HEIGHT}px`,
  width: `${IMAGE_WIDTH}px`,
};

const FlightListItem: React.FC<{
  flight: FlightsList_flight$key;
}> = props => {
  const { t } = useTranslation(["flights", "common"]);
  const flight = useFragment<FlightsList_flight$key>(flightFragment, props.flight);
  const { takeoff, duration } = useFlightDuration(flight);
  const memoizedLatLng: [number, number] = useMemo(() => {
    return [flight.takeoffLocation?.latitude || 0, flight.takeoffLocation?.longitude || 0];
  }, [flight.takeoffLocation?.latitude, flight.takeoffLocation?.longitude]);
  const address = useReverseGeocode({ memoizedLatLng, callerId: "FlightsList" });

  const flightLink = formatOrgPrefixedPath(
    flight.organization?.uuid || "",
    `/fleet/flight/${flight.uuid}?vehicleId=${flight.vehicle?.vehicleId}`
  );
  const logdashPageFlightLink = `https://logdash.skydio.com/?active_tab=flight_plotter_tab_s3&panel.1.name=Media&flight=${flight.uuid}`;

  return (
    <List.Item key={flight.id}>
      <List.Item.Meta
        title={
          <div className="flex gap-2">
            <Link to={flightLink}>{flight.uuid}</Link>
            <Divider orientation="vertical" className="!h-3 self-center bg-gray-200" />
            <Text>
              <a href={logdashPageFlightLink} target="_blank">
                Log Dashboard
              </a>
            </Text>
          </div>
        }
        description={
          <div className="flex w-full flex-col gap-2">
            <div>
              <div className="flex gap-2">
                <FlightMetaDataItem icon={<ClockCircleOutlined />}>
                  {takeoff.format(t("common:format.timestampAt"))}
                  {duration ? " for " + duration.humanize() : null}
                </FlightMetaDataItem>
                <Divider orientation="vertical" className="!h-3 self-center bg-gray-200" />
                <FlightMetaDataItem icon={<EnvironmentOutlined />}>
                  {flight.hasTelemetry
                    ? address
                      ? address
                      : "No address available"
                    : "No telemetry available"}
                </FlightMetaDataItem>
              </div>
            </div>
            <div>
              <div className="flex gap-2">
                {flight.organization && (
                  <>
                    <FlightMetaDataItem icon={<ApartmentOutlined />}>
                      {flight.organization?.name}
                    </FlightMetaDataItem>
                    <Divider orientation="vertical" className="!h-3 self-center bg-gray-200" />
                  </>
                )}
                <FlightMetaDataItem icon={<UserOutlined />} truncate>
                  {formatPilotName(flight.user, flight.takeoffClientType)}
                </FlightMetaDataItem>
                {flight.dock && (
                  <>
                    <Divider orientation="vertical" className="!h-3 self-center bg-gray-200" />
                    <FlightMetaDataItem icon={<DockIcon />}>
                      {flight.dock?.displayName}
                    </FlightMetaDataItem>
                  </>
                )}
                <Divider orientation="vertical" className="!h-3 self-center bg-gray-200" />
                <FlightMetaDataItem icon={<FontAwesomeIcon icon={faDrone} />}>
                  {flight.vehicle?.displayName}
                </FlightMetaDataItem>
              </div>
            </div>
            <div>
              <FlightMetaDataItem icon={<TagsOutlined />}>
                <FlightAnnotations flightRef={flight} />
              </FlightMetaDataItem>
            </div>
          </div>
        }
        avatar={
          flight.dataFiles &&
          flight.dataFiles.edges[0] &&
          flight.dataFiles.edges[0].node && (
            <FileCard
              fileRef={flight.dataFiles.edges[0].node}
              isLastEdge={false}
              size={
                ["VEHICLE_IMAGE", "VEHICLE_RADIOMETRIC_IMAGE", "VEHICLE_IR_IMAGE"].includes(
                  flight.dataFiles.edges[0].node.kind
                )
                  ? PHOTO_SIZE
                  : VIDEO_SIZE
              }
              readonly
            />
          )
        }
      />
    </List.Item>
  );
};
export interface FlightsPageContextType {
  setCount?: (count: number | undefined) => void;
}
export const FlightsPageContext = createContext<FlightsPageContextType | null>(null);

const FlightsList: React.FC<{
  queryReference: PreloadedQuery<FlightsListAdminQuery>;
}> = ({ queryReference }) => {
  const flightsPageContext = useContext(FlightsPageContext);

  const queryData = usePreloadedQuery<FlightsListAdminQuery>(query, queryReference);
  // TODO: Move to parent and pass in via prop?
  const [searchTerm, setSearchTerm] = useQueryStringState("search");
  const [localSearchTerm, setLocalSearchTerm] = useQueryStringState("flsearch", "");

  useEffect(() => {
    if (localSearchTerm) {
      setSearchTerm("");
    }
  }, [localSearchTerm, setSearchTerm]);

  const [flightInput, handleFlightInput] = useDebouncedState<string>(
    localSearchTerm,
    setLocalSearchTerm
  );
  const annotationCategoryUuids = useAnnotationsFromURL();

  const paginationQuery = usePaginationFragment<FlightsRefetchQuery, FlightsList_flights$key>(
    flightsFragment,
    queryData
  );

  const [paginationProps, data, resetPage] = useRelayPagination(
    paginationQuery,
    pageSize,
    paginationQuery.data?.flights
  );

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();
  useEffect(() => {
    startTransition(() => {
      resetPage();
      let count = pageSize;
      if (
        !searchTerm &&
        !localSearchTerm &&
        (!annotationCategoryUuids || annotationCategoryUuids?.length == 0)
      )
        count = 0;
      refetch({
        count: count,
        searchTerm: localSearchTerm ? localSearchTerm : searchTerm,
        annotationCategoryUuids,
      });
    });
  }, [refetch, resetPage, searchTerm, annotationCategoryUuids, localSearchTerm]);

  useEffect(() => {
    if (flightsPageContext?.setCount) {
      flightsPageContext?.setCount(
        paginationQuery.data.flights?.edges.length ? paginationProps.totalCount : 0
      );
    }
  }, [
    flightsPageContext,
    flightsPageContext?.setCount,
    paginationProps.totalCount,
    paginationQuery.data.flights?.edges.length,
  ]);

  return (
    <>
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
        <div className="flex grow flex-wrap gap-2">
          <Input
            placeholder="Search"
            className={styles.search}
            value={flightInput}
            onValueChange={handleFlightInput}
            isClearable
            isSearch
          />
          <AnnotationCategoryFilter displayOrgTags={false} />
        </div>
        <Pagination {...paginationProps} />
      </div>
      {!paginationQuery.data.flights || paginationQuery.data.flights.edges.length == 0 ? (
        <AdminSearchEmptyState
          hasResults={paginationProps.totalCount != 0}
          hasFilters={true}
          isLoading={isPending}
        />
      ) : (
        <List
          size="small"
          bordered
          dataSource={data}
          loading={isPending}
          renderItem={edge => (edge.node ? <FlightListItem flight={edge.node} /> : <></>)}
        ></List>
      )}
    </>
  );
};

export const FlightsPage = () => {
  const [queryReference, fetchFlights] = useQueryLoader<FlightsListAdminQuery>(query);

  useEffect(() => {
    fetchFlights({ count: 0 });
  }, [fetchFlights]);

  return queryReference && <FlightsList queryReference={queryReference} />;
};

export default FlightsList;
